/**
 * General Messaging API Service for Account Executive
 * Handles messaging with Super Admin and other roles via /api/messages
 */
import { BASE_URL } from '../components/BaseUrl';

class GeneralMessagingApi {
  constructor(token) {
    this.token = token;
    this.apiBaseUrl = BASE_URL;
  }

  getHeaders() {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.token}`,
    };
  }

  /**
   * Get all threads from general messaging system
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 20)
   * @returns {Promise<Object>} Response with threads and pagination
   */
  async getThreads(page = 1, limit = 20) {
    const response = await fetch(
      `${this.apiBaseUrl}/messages/threads?page=${page}&limit=${limit}`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    );
    return response.json();
  }

  /**
   * Get eligible users for messaging (filtered by role)
   * @param {string} role - Role filter: 'superadmin' or 'company_admin'
   * @param {string} search - Search query for name/email
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 500)
   * @returns {Promise<Object>} Response with eligible users
   */
  async getEligibleUsers(role, search = '', page = 1, limit = 500) {
    const response = await fetch(
      `${this.apiBaseUrl}/messages/eligible-users?role=${role}&search=${encodeURIComponent(search)}&page=${page}&limit=${limit}`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    );
    const data = await response.json();
    console.log(`📋 getEligibleUsers response for role ${role}:`, data);
    return data;
  }

  /**
   * Create a new thread or get existing thread
   * @param {number} receiverId - Receiver user ID
   * @param {string} receiverRole - Receiver role ('superadmin' or 'company_admin')
   * @returns {Promise<Object>} Response with thread data
   */
  async createOrGetThread(receiverId, receiverRole) {
    const response = await fetch(
      `${this.apiBaseUrl}/messages/threads`,
      {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ receiverId, receiverRole }),
      }
    );
    const data = await response.json();
    console.log(`📋 createOrGetThread response for role ${receiverRole}:`, data);
    return data;
  }

  /**
   * Get messages in a specific thread
   * @param {number} threadId - Thread ID
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 50)
   * @returns {Promise<Object>} Response with messages and pagination
   */
  async getThreadMessages(threadId, page = 1, limit = 50) {
    const response = await fetch(
      `${this.apiBaseUrl}/messages/threads/${threadId}/messages?page=${page}&limit=${limit}`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    );
    return response.json();
  }

  /**
   * Send a message in a thread
   * @param {number} threadId - Thread ID
   * @param {string} content - Message content
   * @param {string} messageType - Message type: 'text', 'image', 'file', 'system'
   * @param {string|null} fileUrl - File URL (optional)
   * @param {string|null} fileName - File name (optional)
   * @param {number|null} fileSize - File size in bytes (optional)
   * @param {string|null} mimeType - MIME type (optional)
   * @returns {Promise<Object>} Response with sent message data
   */
  async sendMessage(threadId, content, messageType = 'text', fileUrl = null, fileName = null, fileSize = null, mimeType = null) {
    const response = await fetch(
      `${this.apiBaseUrl}/messages/threads/${threadId}/messages`,
      {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ 
          content, 
          messageType,
          fileUrl,
          fileName,
          fileSize,
          mimeType
        }),
      }
    );
    return response.json();
  }

  /**
   * Mark all messages in a thread as read
   * @param {number} threadId - Thread ID
   * @returns {Promise<Object>} Response with marked count
   */
  async markAllAsRead(threadId) {
    const response = await fetch(
      `${this.apiBaseUrl}/messages/threads/${threadId}/read`,
      {
        method: 'PUT',
        headers: this.getHeaders(),
      }
    );
    return response.json();
  }

  /**
   * Get unread count
   * @returns {Promise<Object>} Response with total unread count
   */
  async getUnreadCount() {
    const response = await fetch(
      `${this.apiBaseUrl}/messages/unread-count`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    );
    return response.json();
  }
}

export default GeneralMessagingApi;

